import { useEffect, useId, type FormEvent } from 'react';
import { X } from 'lucide-react';
import type { Period } from '@casetrace/shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { PeriodColorPicker } from './period-color-picker';
import { usePeriodEditorForm } from './use-period-editor-form';

interface PeriodEditorProps {
  readonly caseId: string;
  readonly period: Period | null;
  readonly onClose: () => void;
}

export function PeriodEditor({ caseId, period, onClose }: PeriodEditorProps) {
  const titleId = useId();
  const form = usePeriodEditorForm(caseId, period, onClose);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    void form.submit();
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-md rounded-xl border border-white/10 bg-surface-raised p-5 shadow-2xl"
      >
        <EditorHeader titleId={titleId} isEdit={form.isEdit} onClose={onClose} />
        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <Input
            label="Tittel"
            value={form.label}
            onChange={(event) => form.setLabel(event.target.value)}
            onBlur={() => form.setTitleTouched(true)}
            error={form.titleTouched ? form.labelError : undefined}
            maxLength={120}
            autoFocus
            required
          />

          <DescriptionField value={form.description} onChange={form.setDescription} />

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Fra dato"
              type="date"
              value={form.fromDate}
              onChange={(event) => form.setFromDate(event.target.value)}
              required
            />
            <Input
              label="Til dato"
              type="date"
              value={form.toDate}
              onChange={(event) => form.setToDate(event.target.value)}
              error={form.rangeError}
              required
            />
          </div>

          <PeriodColorPicker value={form.color} onChange={form.setColor} />

          {form.displayError && <p className="text-xs text-danger/80">{form.displayError}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={onClose} disabled={form.pending}>
              Avbryt
            </Button>
            <Button type="submit" disabled={!form.canSubmit}>
              {form.pending ? 'Lagrer…' : 'Lagre'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditorHeader({
  titleId,
  isEdit,
  onClose,
}: {
  readonly titleId: string;
  readonly isEdit: boolean;
  readonly onClose: () => void;
}) {
  return (
    <div className="mb-4 flex items-start justify-between gap-2">
      <h2 id={titleId} className="text-sm font-semibold text-text-primary">
        {isEdit ? 'Rediger tidsslott' : 'Ny tidsslott'}
      </h2>
      <button
        type="button"
        onClick={onClose}
        className="rounded-md p-1 text-text-tertiary hover:bg-white/6 hover:text-text-secondary"
        aria-label="Lukk"
      >
        <X size={15} />
      </button>
    </div>
  );
}

function DescriptionField({
  value,
  onChange,
}: {
  readonly value: string;
  readonly onChange: (next: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor="period-description" className="text-xs font-medium text-[#8b8d9a]">
        Beskrivelse (valgfri)
      </label>
      <textarea
        id="period-description"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={3}
        maxLength={4000}
        className="w-full resize-y rounded-md border border-white/[0.1] bg-[#1a1b23] px-3 py-2 text-sm text-[#ededf0] outline-none focus:border-accent/60 focus:ring-1 focus:ring-accent/20"
      />
    </div>
  );
}
